/**
 * Auto-injection of inline CTAs into blog posts that don't already have one.
 *
 * CTAs are matched to reader intent based on blog category, using the same
 * `<cta>` HAST node that RehypeReact already maps to the ContentCTA component.
 */

const SIGNUP_BASE = "https://manage.app.preset.io/starter-registration/"
const CONTACT_SALES = "https://preset.io/contact-sales/"
const BI_GUIDE = "https://preset.io/download-popular-BI-guide"

// ---------------------------------------------------------------------------
// Category → CTA mapping
// ---------------------------------------------------------------------------

const CTA_PRESETS = {
  starter: {
    image: "/images/cta/image-1.png",
    title: "Try it yourself — free forever",
    linkText: "Start for free now",
    baseUrl: SIGNUP_BASE,
    body: "Build beautiful charts and dashboards in cloud-hosted Apache Superset.",
    utmCampaign: "CTA+box",
  },
  deepDive: {
    image: "/images/cta/idea.png",
    title: "See how Preset handles this",
    linkText: "Start for free now",
    baseUrl: SIGNUP_BASE,
    body: "Build beautiful charts and dashboards in cloud-hosted Apache Superset.",
    utmCampaign: "CTA+box",
  },
  caseStudy: {
    image: "/images/cta/preset.jpg",
    title: "Get similar results for your team",
    linkText: "Book a demo",
    baseUrl: CONTACT_SALES,
    body: "See how Preset can help your organization unlock the power of data.",
    utmCampaign: "CTA+box",
  },
  practitioner: {
    image: "/images/cta/preset.jpg",
    title: "Skip the ops — try Preset free",
    linkText: "Start for free now",
    baseUrl: SIGNUP_BASE,
    body: "All the features of Superset (and more) without the hassles of scaling, managing, or upgrading.",
    utmCampaign: "CTA+box",
  },
  newsletter: {
    image: "/images/cta/preset.jpg",
    title: "Stay in the loop",
    linkText: "Start for free now",
    baseUrl: SIGNUP_BASE,
    body: "Try Preset free and stay up to date with the latest in Apache Superset.",
    utmCampaign: "CTA+box",
  },
  community: {
    image: "/images/cta/preset.jpg",
    title: "Join the Preset community",
    linkText: "Start for free now",
    baseUrl: SIGNUP_BASE,
    body: "Get started with Apache Superset the easy way — free for five users, forever.",
    utmCampaign: "CTA+box",
  },
  security: {
    image: "/images/cta/preset.jpg",
    title: "Talk to our security team",
    linkText: "Contact us",
    baseUrl: CONTACT_SALES,
    body: "Learn how Preset can help your organization meet its security and compliance requirements.",
    utmCampaign: "CTA+box",
  },
  dashboards: {
    image: "/images/cta/image-1.png",
    title: "Build your first dashboard — free",
    linkText: "Start for free now",
    baseUrl: SIGNUP_BASE,
    body: "Create beautiful charts and dashboards in cloud-hosted Apache Superset.",
    utmCampaign: "CTA+box",
  },
  comparison: {
    image: "/images/cta/bi-vendor-comparison.png",
    title: "Find the right BI tool for your org",
    linkText: "Download now",
    baseUrl: BI_GUIDE,
    body: "Evaluate popular analytics software tools such as Apache Superset, Tableau, Looker, and more.",
    utmCampaign: "comparison+CTA+box",
  },
}

const CATEGORY_TO_PRESET = {
  "DATA VISUALIZATION 101": "starter",
  "DATA LITERACY": "starter",
  "SUPERSET 101": "starter",
  "DEEP DIVE": "deepDive",
  "DATA ENGINEERING": "deepDive",
  "CASE STUDIES": "caseStudy",
  SUPERSET: "practitioner",
  "SUPERSET DEVELOPERS": "practitioner",
  ANNOUNCEMENTS: "newsletter",
  NEWSLETTER: "newsletter",
  COMMUNITY: "community",
  SECURITY: "security",
  DASHBOARDS: "dashboards",
}

// ---------------------------------------------------------------------------
// Public helpers
// ---------------------------------------------------------------------------

/**
 * Returns the CTA config for a given blog post, or null if none applies.
 * Comparison posts (slug contains "vs-" or "alternative") get a special CTA.
 */
function getCtaConfigForPost(category, slug) {
  const normalizedSlug = (slug || "").toLowerCase()
  const isComparison =
    normalizedSlug.includes("-vs-") || normalizedSlug.includes("alternative")

  const presetKey = isComparison
    ? "comparison"
    : CATEGORY_TO_PRESET[(category || "").toUpperCase()] || "starter"

  const preset = CTA_PRESETS[presetKey]
  if (!preset) return null

  // Build UTM-tagged URL following existing convention:
  // utm_source=organic&utm_medium=blog&utm_campaign=CTA+box&utm_id=cta&utm_content={slug}
  const sep = preset.baseUrl.includes("?") ? "&" : "?"
  const utmContent = encodeURIComponent(`${normalizedSlug}+CTA+box`).replace(
    /%2B/g,
    "+"
  )
  const url = `${preset.baseUrl}${sep}utm_source=organic&utm_medium=blog&utm_campaign=${preset.utmCampaign}&utm_id=cta&utm_content=${utmContent}`

  return {
    image: preset.image,
    title: preset.title,
    linkText: preset.linkText,
    linkUrl: url,
    body: preset.body,
  }
}

/**
 * Recursively checks whether the HAST tree already contains a `<cta>` node.
 */
function htmlAstContainsCta(node) {
  if (!node) return false
  if (node.tagName === "cta") return true
  if (node.children) {
    return node.children.some(htmlAstContainsCta)
  }
  return false
}

/**
 * Returns a cloned copy of `htmlAst` with a synthetic `<cta>` node injected
 * at approximately 20 % depth (measured by top-level block elements).
 *
 * The injected node is a standard HAST element node that RehypeReact will
 * render via the existing `cta → ContentCTA` mapping.
 */
function injectCtaIntoAst(htmlAst, ctaConfig) {
  if (!htmlAst || !ctaConfig) return htmlAst

  // Deep-clone so we never mutate the original AST
  const ast = JSON.parse(JSON.stringify(htmlAst))
  const children = ast.children || []

  // Identify block-level element indices
  const blockTags = new Set([
    "p",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "ul",
    "ol",
    "blockquote",
    "pre",
    "table",
    "div",
    "hr",
    "figure",
  ])

  const blockIndices = []
  for (let i = 0; i < children.length; i++) {
    if (children[i].type === "element" && blockTags.has(children[i].tagName)) {
      blockIndices.push(i)
    }
  }

  if (blockIndices.length === 0) return ast

  // Target: 20 % of block elements, minimum after the 1st block
  const targetBlock = Math.max(1, Math.floor(blockIndices.length * 0.2))
  const insertAfterIndex =
    blockIndices[Math.min(targetBlock, blockIndices.length - 1)]

  // Build the synthetic <cta> HAST node
  const ctaNode = {
    type: "element",
    tagName: "cta",
    properties: {
      image: ctaConfig.image,
      title: ctaConfig.title,
      "link-text": ctaConfig.linkText,
      "link-url": ctaConfig.linkUrl,
    },
    children: ctaConfig.body ? [{ type: "text", value: ctaConfig.body }] : [],
  }

  // Insert immediately after the target block element
  children.splice(insertAfterIndex + 1, 0, ctaNode)
  ast.children = children

  return ast
}

module.exports = {
  getCtaConfigForPost,
  htmlAstContainsCta,
  injectCtaIntoAst,
  // Exported for testing
  CTA_PRESETS,
}
